import { useEffect, useState } from 'react';
import { Icon } from '../icons';
import { api } from '../../lib/api';
import type { JobDoc } from '../../lib/types';

interface Props {
  jobId: string | null;
  job: JobDoc | null;
  onRestart: () => void;
}

export function SuccessScreen({ jobId, job, onRestart }: Props) {
  const [downloadUrl, setDownloadUrl] = useState<string | null>(
    job?.download?.signedUrl ?? null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (job?.download?.signedUrl) {
      setDownloadUrl(job.download.signedUrl);
    }
  }, [job?.download?.signedUrl]);

  async function refreshUrl() {
    if (!jobId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.signDownloadUrl({ jobId });
      setDownloadUrl(res.url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'שגיאה ביצירת קישור הורדה');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="screen">
      <div className="screen-header">
        <div style={{ width: 38 }} />
        <div className="step-counter">הסרטון מוכן</div>
        <div style={{ width: 38 }} />
      </div>
      <div className="screen-scroll">
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 999,
              background: 'var(--accent)',
              color: 'white',
              display: 'inline-grid',
              placeItems: 'center',
              boxShadow: '0 12px 30px oklch(0.62 0.13 45 / 0.4)',
            }}
          >
            <Icon.Check size={32} />
          </div>
        </div>
        <h1 className="h1" style={{ textAlign: 'center', marginTop: 18, fontSize: 26 }}>
          הסרטון מוכן!
        </h1>
        <p className="lede" style={{ textAlign: 'center' }}>
          שמרנו את הסרטון בחשבון שלכם. אפשר להוריד או לשתף עכשיו.
        </p>

        <div className="video-preview" style={{ maxHeight: 280 }}>
          <div
            style={{
              width: '100%',
              height: '100%',
              background:
                'linear-gradient(135deg, oklch(0.7 0.1 45) 0%, oklch(0.55 0.12 35) 100%)',
            }}
          />
          <div className="play-overlay">
            <div className="play-circ">
              <Icon.Play size={26} />
            </div>
          </div>
        </div>

        {downloadUrl ? (
          <a
            href={downloadUrl}
            className="btn-primary"
            style={{ marginTop: 18, textDecoration: 'none' }}
            download
          >
            <Icon.Download size={18} />
            הורדת הסרטון (MP4)
          </a>
        ) : (
          <button
            className="btn-primary"
            style={{ marginTop: 18 }}
            onClick={refreshUrl}
            disabled={busy || !jobId}
          >
            <Icon.Download size={18} />
            {busy ? 'מכינים קישור...' : 'יצירת קישור הורדה'}
          </button>
        )}

        {error && (
          <div
            className="tip-row"
            style={{ background: 'oklch(0.92 0.05 30 / 0.6)', color: 'var(--ink)' }}
          >
            <div>{error}</div>
          </div>
        )}

        <div
          style={{
            marginTop: 20,
            fontFamily: 'Heebo',
            fontWeight: 600,
            fontSize: 14,
            color: 'var(--ink-soft)',
          }}
        >
          שיתוף מהיר
        </div>
        <div className="share-row">
          <button className="share-btn">
            <div className="ic" style={{ background: '#25D366' }}>
              <Icon.Whatsapp size={20} />
            </div>
            WhatsApp
          </button>
          <button className="share-btn">
            <div
              className="ic"
              style={{ background: 'linear-gradient(135deg, #f09433, #dc2743 50%, #bc1888)' }}
            >
              <Icon.Instagram size={20} />
            </div>
            Instagram
          </button>
          <button className="share-btn">
            <div className="ic" style={{ background: 'var(--ink)' }}>
              <Icon.Link size={20} />
            </div>
            קישור
          </button>
          <button className="share-btn">
            <div className="ic" style={{ background: 'var(--accent)' }}>
              <Icon.More size={20} />
            </div>
            עוד
          </button>
        </div>

        <button
          onClick={onRestart}
          style={{
            marginTop: 22,
            width: '100%',
            padding: 14,
            background: 'transparent',
            border: 'none',
            color: 'var(--accent-deep)',
            fontFamily: 'Heebo',
            fontWeight: 600,
            fontSize: 15,
            cursor: 'pointer',
          }}
        >
          + יצירת סרטון נוסף
        </button>
      </div>
    </div>
  );
}
